import {
  Button,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { X } from "lucide-react";
import { useState } from "react";
import { type Api, type ContextClaim, apis, claims } from "../../data/api";
import { useAction, useLoad } from "../../data/data";
import { IconButton } from "../../ui/components/IconButton";
import {
  Empty,
  Failure,
  Field,
  FormRow,
  Loading,
  Mono,
  PageHeader,
  Section,
  Tag,
} from "../../ui/components/primitives";
import type { TenantPageProps } from "../pages";

// ---
// Context claims
//
// Tenant self-service, because a context claim cannot escalate: it is emitted inside the `ctx`
// container and validators never read it. That is why this exists while the dimension registry
// stays platform-only — a dimension narrows authority, and a tenant adding one from a UI would
// have its own tokens rejected until every validator caught up.
// ---

// The audiences offered come from this tenant's registry, not from a list in the console. A
// claim narrowed to an audience the tenant does not own would never be emitted, and one it does
// own was unofferable if the console had not been taught about it.

export function Claims({ tenant, token, scopes }: TenantPageProps): React.JSX.Element {
  const list = useLoad(() => claims.list(tenant, token), [tenant, token]);
  // Best effort: a caller without `permission:read` still gets "All audiences", which is the
  // common case and the one that needs no list.
  const apiList = useLoad(() => apis.list(tenant, token), [tenant, token]);
  const action = useAction(() => list.reload());
  const [draft, setDraft] = useState<ContextClaim>({
    name: "",
    source: "static",
    value: "",
    audiences: null,
  });

  const canManage = scopes.includes("claim:manage");
  const audiences = apiList.data ?? [];

  return (
    <>
      <PageHeader title="Claims" />

      {canManage && (
        <Section title="Add a context claim">
          <FormRow>
            <Field label="Name" sx={{ flex: "1 1 160px" }}>
              <TextField
                size="small"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder="cost_center"
              />
            </Field>
            <Field label="Source" sx={{ flex: "1 1 150px" }}>
              <Select
                size="small"
                value={draft.source}
                onChange={(event) => setDraft({ ...draft, source: event.target.value })}
              >
                <MenuItem value="static">Static value</MenuItem>
                <MenuItem value="user_attribute">User attribute</MenuItem>
              </Select>
            </Field>
            {draft.source === "static" ? (
              <Field label="Value" sx={{ flex: "1 1 150px" }}>
                <TextField
                  size="small"
                  value={draft.value ?? ""}
                  onChange={(event) => setDraft({ ...draft, value: event.target.value })}
                />
              </Field>
            ) : (
              <Field label="Attribute" sx={{ flex: "1 1 150px" }}>
                <Select
                  size="small"
                  value={draft.value ?? "email"}
                  onChange={(event) => setDraft({ ...draft, value: event.target.value })}
                >
                  <MenuItem value="email">email</MenuItem>
                  <MenuItem value="username">username</MenuItem>
                  <MenuItem value="name">name</MenuItem>
                </Select>
              </Field>
            )}
            <Field label="Audience" sx={{ flex: "1 1 200px" }}>
              <Select
                size="small"
                displayEmpty
                value={draft.audiences?.[0] ?? ""}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    audiences: event.target.value === "" ? null : [event.target.value],
                  })
                }
              >
                <MenuItem value="">All audiences</MenuItem>
                {audiences.map((api: Api) => (
                  <MenuItem key={api.resource} value={api.resource}>
                    {api.resource.replace("https://", "")}
                  </MenuItem>
                ))}
              </Select>
            </Field>
            <Button
              variant="contained"
              disabled={draft.name === "" || action.busy}
              onClick={() =>
                action.run(async () => {
                  await claims.upsert(tenant, token, draft);
                  setDraft({ name: "", source: "static", value: "", audiences: null });
                })
              }
            >
              Save
            </Button>
          </FormRow>
          <Failure error={action.error} />
        </Section>
      )}

      <Section flush title={`Claims · ${list.data?.length ?? 0}`}>
        <Failure error={list.error} />
        {list.pending ? (
          <Loading what="claims" />
        ) : list.data?.length === 0 ? (
          <Empty>
            None. Tokens carry no <Mono>ctx</Mono> container until one exists.
          </Empty>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.data?.map((claim: ContextClaim) => (
                <TableRow key={claim.name} hover>
                  <TableCell>
                    <Mono>{claim.name}</Mono>
                  </TableCell>
                  <TableCell>
                    <Tag label={claim.source} />
                  </TableCell>
                  <TableCell>
                    <Mono>{claim.value}</Mono>
                  </TableCell>
                  <TableCell>{claim.audiences?.[0]?.replace("https://", "") ?? "All"}</TableCell>
                  <TableCell align="right">
                    {canManage && (
                      <IconButton
                        tone="danger"
                        title={`Remove ${claim.name}`}
                        onClick={() => action.run(() => claims.remove(tenant, token, claim.name))}
                      >
                        <X size={14} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </>
  );
}
